// React imports
import { useMemo } from 'react';

// Types
import AddOnService from '@spike/addon-service-model';
import Service from 'model/Service';
import { EnhancedProduct } from './useDiscountForm';

interface ItemDisplay<T> {
    getTitle: (item: T) => string;
    getSubtitle: (item: T) => string;
    getPrice: (item: T) => string;
    getTitleFull?: (item: T) => string;
    getSubtitleFull?: (item: T) => string;
}

interface UseItemDisplayConfigsReturn {
    serviceDisplay: ItemDisplay<Service>;
    addonDisplay: ItemDisplay<AddOnService>;
    productDisplay: ItemDisplay<EnhancedProduct>;
}

/**
 * useItemDisplayConfigs - Item Display Formatting Hook
 *
 * Purpose:
 * Provides standardized display formatting functions for different item types
 * (services, add-ons, products) used in ItemSelectionDrawer components.
 *
 * Responsibilities:
 * - Defines how to extract and format titles for each item type
 * - Defines how to extract and format subtitles for each item type
 * - Defines how to format pricing information for each item type
 * - Provides full-length versions for products (truncated vs full display)
 * - Handles edge cases (variable pricing, missing data, long names)
 *
 * Display Configurations:
 *
 * Services:
 * - Title: Service name
 * - Subtitle: Duration (e.g., "2 hour 30 min") or "Variable Duration"
 * - Price: "Variable Price" or "Fixed Price" indicator
 *
 * Add-ons:
 * - Title: Add-on name
 * - Subtitle: "Add-on Service" label
 * - Price: Formatted price (e.g., "$25.00")
 *
 * Products:
 * - Title: Product name (truncated to 30 chars if needed)
 * - Subtitle: Description (truncated to 50 chars) or "Product"
 * - Price: Formatted price or "Price not set"
 * - Full versions: Complete title and subtitle without truncation
 *
 * Key Features:
 * - Memoized configurations for performance
 * - Type-safe with specific interfaces for each item type
 * - Consistent formatting across all selection drawers
 * - Handles missing or undefined data gracefully
 *
 * When to use:
 * Use this hook to get display configuration objects for ItemSelectionDrawer
 * components. Pass the appropriate config (serviceDisplay, addonDisplay, productDisplay)
 * to the corresponding drawer.
 *
 * @returns {UseItemDisplayConfigsReturn} Object containing display configs for services, add-ons, and products
 */
export const useItemDisplayConfigs = (): UseItemDisplayConfigsReturn => {
    const serviceDisplay = useMemo<ItemDisplay<Service>>(
        () => ({
            getTitle: (service: Service) => service.name,
            getSubtitle: (service: Service) =>
                'simple' in service.duration && service.duration.simple !== undefined
                    ? service.duration.simple?.hours + ' hour ' + service.duration.simple?.minutes + ' min'
                    : 'Variable Duration',
            getPrice: (service: Service) =>
                service.pricing.variablePrice?.prices?.length && service.pricing.variablePrice.prices.length > 0
                    ? 'Variable Price'
                    : 'Fixed Price'
        }),
        []
    );

    const addonDisplay = useMemo<ItemDisplay<AddOnService>>(
        () => ({
            getTitle: (addon: AddOnService) => addon.name,
            getSubtitle: () => 'Add-on Service',
            getPrice: (addon: AddOnService) => {
                return `$${Number(addon.price).toFixed(2)}`;
            }
        }),
        []
    );

    const productDisplay = useMemo<ItemDisplay<EnhancedProduct>>(
        () => ({
            getTitle: (product: EnhancedProduct) => {
                const productName = product.businessProduct?.name || product.name;
                return productName.length > 30 ? productName.substring(0, 30) + '...' : productName;
            },
            getSubtitle: (product: EnhancedProduct) => {
                const description = product.businessProduct?.description;
                if (!description) return 'Product';
                return description.length > 50 ? description.substring(0, 50) + '...' : description;
            },
            getPrice: (product: EnhancedProduct) =>
                product.businessProduct?.pricing?.price
                    ? `$${Number(product.businessProduct.pricing.price).toFixed(2)}`
                    : 'Price not set',
            getTitleFull: (product: EnhancedProduct) => product.businessProduct?.name || product.name,
            getSubtitleFull: (product: EnhancedProduct) => product.businessProduct?.description || 'Product'
        }),
        []
    );

    return {
        serviceDisplay,
        addonDisplay,
        productDisplay
    };
};
